/**
 * THE ANIMATION LAYER (ADR 0009; phase brief section 10). One rAF loop for
 * the whole surface; ATTRIBUTES only (transform), never document classes —
 * styling across the <use> boundary is impossible and layout-triggering
 * properties are banned. Honors prefers-reduced-motion (discrete states
 * still change; they stop animating) and suspends entirely while the tab
 * is hidden.
 *
 * Channels:
 *  - ambient idle: a slow breathe on every [data-actor] (translateY)
 *  - walk cycle:  [data-pose="walk"] additionally shuttles a few cm
 *  - the clock sweep: when the wall clock's authored time changes between
 *    renders, the hands SWEEP from the old time (the learner watches the
 *    cost happen) rather than snapping
 *  - the stilling: [data-beat="stilling"] pauses ALL idle motion for a
 *    beat — reserved for the GREAT band and nothing else (ADR 0009)
 */

interface ClockMemory {
  hour: number;
  minute: number;
}

const SWEEP_MS = 1100;
const STILL_MS = 2200;

function angles(hour: number, minute: number): { hourDeg: number; minuteDeg: number } {
  return {
    minuteDeg: (minute / 60) * 360,
    hourDeg: (((hour % 12) + minute / 60) / 12) * 360,
  };
}

export function attachSectionAnimation(container: HTMLElement): () => void {
  const reduced = typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduced) return () => undefined;

  let raf = 0;
  let running = false;
  const clockMemory: ClockMemory = { hour: Number.NaN, minute: Number.NaN };
  let sweepStart = 0;
  let sweepFrom: ClockMemory | null = null;
  let stillUntil = 0;
  let lastBeatId: string | null = null;

  const frame = (now: number): void => {
    raf = requestAnimationFrame(frame);

    const root = container.querySelector("svg");
    if (root === null) return;

    // ---- the stilling: a new stilling beat pauses everything for a beat ----
    const beatHost = root.querySelector<SVGGElement>("[data-beat='stilling']");
    const beatId = beatHost?.getAttribute("data-beat-id") ?? null;
    if (beatId !== null && beatId !== lastBeatId) {
      lastBeatId = beatId;
      stillUntil = now + STILL_MS;
    }
    const stilled = now < stillUntil;

    // ---- sky drift: clouds cross slowly, birds glide with a faint bob.
    // Position wraps across the drawing width (data-drift-span). ----
    const drifters = root.querySelectorAll<SVGGElement>("[data-ambient]");
    drifters.forEach((drifter) => {
      const span = Number(drifter.getAttribute("data-drift-span"));
      const seed = Number(drifter.getAttribute("data-drift-seed")) || 0;
      if (!Number.isFinite(span) || span <= 0) return;
      const isBird = drifter.getAttribute("data-ambient") === "bird";
      const speed = isBird ? 26 + seed * 5 : 5 + seed * 1.6; // cm per second
      const raw = (now / 1000) * speed + seed * span * 0.31;
      const dx = (raw % (span * 1.3)) - span * 0.15;
      const dy = isBird ? Math.sin(now / 900 + seed) * 9 : 0;
      drifter.setAttribute("transform", `translate(${String(Math.round(dx * 100) / 100)},${String(Math.round(dy * 100) / 100)})`);
    });

    // ---- ambient idle + walk. A plan playback (ADR 0014, the dev stage)
    // adds a travel offset through data-offset-x/y on the actor: the
    // section draws the actor at its destination and the offset carries it
    // back along the path; this loop only sums the two. ----
    const actors = root.querySelectorAll<SVGElement>("[data-actor]");
    actors.forEach((actor, index) => {
      const offsetX = Number(actor.getAttribute("data-offset-x")) || 0;
      const offsetY = Number(actor.getAttribute("data-offset-y")) || 0;
      if (stilled) {
        actor.setAttribute("transform", `translate(${String(offsetX)},${String(offsetY)})`);
        return;
      }
      const phase = index * 1.7;
      const bob = Math.sin(now / 1400 + phase) * 1.6;
      let dx = 0;
      if (actor.getAttribute("data-pose") === "walk" || actor.hasAttribute("data-travelling")) {
        dx = Math.sin(now / 700 + phase) * 16;
      }
      actor.setAttribute("transform", `translate(${String(Math.round((dx + offsetX) * 100) / 100)},${String(Math.round((bob + offsetY) * 100) / 100)})`);
    });

    // ---- the clock sweep ----
    const clock = root.querySelector<SVGGElement>("[data-part='timepiece']");
    if (clock !== null) {
      const hour = Number(clock.getAttribute("data-hour"));
      const minute = Number(clock.getAttribute("data-minute"));
      if (Number.isFinite(hour) && Number.isFinite(minute)) {
        if (Number.isNaN(clockMemory.hour)) {
          clockMemory.hour = hour;
          clockMemory.minute = minute;
        } else if (hour !== clockMemory.hour || minute !== clockMemory.minute) {
          sweepFrom = { ...clockMemory };
          sweepStart = now;
          clockMemory.hour = hour;
          clockMemory.minute = minute;
        }
        const cx = clock.getAttribute("data-cx") ?? "0";
        const cy = clock.getAttribute("data-cy") ?? "0";
        const hourHand = clock.querySelector<SVGElement>("[data-part='hand-hour']");
        const minuteHand = clock.querySelector<SVGElement>("[data-part='hand-minute']");
        if (sweepFrom !== null && now - sweepStart < SWEEP_MS) {
          const t = (now - sweepStart) / SWEEP_MS;
          const ease = 1 - (1 - t) * (1 - t);
          const from = angles(sweepFrom.hour, sweepFrom.minute);
          const to = angles(clockMemory.hour, clockMemory.minute);
          // sweep forward: hands are DRAWN at the target; rotate them back
          // toward the old time and let the rotation decay to zero.
          const hourDelta = ((from.hourDeg - to.hourDeg + 540) % 360) - 180;
          const minuteDelta = ((from.minuteDeg - to.minuteDeg + 540) % 360) - 180;
          hourHand?.setAttribute("transform", `rotate(${String(hourDelta * (1 - ease))} ${cx} ${cy})`);
          minuteHand?.setAttribute("transform", `rotate(${String(minuteDelta * (1 - ease))} ${cx} ${cy})`);
        } else {
          hourHand?.removeAttribute("transform");
          minuteHand?.removeAttribute("transform");
          sweepFrom = null;
        }
      }
    }
  };

  const start = (): void => {
    if (!running) {
      running = true;
      raf = requestAnimationFrame(frame);
    }
  };
  const stop = (): void => {
    if (running) {
      running = false;
      cancelAnimationFrame(raf);
    }
  };
  const onVisibility = (): void => {
    if (document.hidden) stop();
    else start();
  };

  document.addEventListener("visibilitychange", onVisibility);
  start();
  return () => {
    stop();
    document.removeEventListener("visibilitychange", onVisibility);
  };
}
